import { useEffect, useState } from "react";
import { Button, Field, Input, Label } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { handleEnterKey, handleEnterSpaceKey } from "../../../app/keyboardUtils";
import { isAdminRole, isHigherThanDistrictAdmin, UserRole } from "../../../app/userSlice";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import { ClassLinkOrCleverIcon } from "../../../components/ClassLinkOrCleverIcon";
import RequiredField from "../../../components/RequiredField";
import ModalFrame from "../../../components/ModalFrame";
import Select from "../../../components/input/Select";
import { useGetGradesQuery } from "../../lookup/lookupApiSlice";
import { Ethnicity, Gender, useEditUserMutation, UserEditModel } from "../../users/usersApiSlice";
import { EditUserViewModel } from "../usersApiSlice";
import { externalIdLabel } from "../usersUtil";
import UserOutsideSiteSettingsForm from "./UserOutsideSiteSettingsForm";

const YesNoOptions: SimpleOption[] = [
	{ value: "", label: "--" },
	{ value: "true", label: "Yes" },
	{ value: "false", label: "No" },
];

interface Props {
	userInfo: EditUserViewModel;
	onClose: () => void;
}

export default function EditUserInformationForm(props: Props) {
	const { config } = useAppSelector((state) => state.site);
	const todayDate = new Date().toISOString().slice(0, 10);
	const user = props.userInfo.editModel;

	const [genderOptions, setGenderOptions] = useState<SimpleOption[]>([]);
	const [ethnicityOptions, setEthnicityOptions] = useState<SimpleOption[]>([]);
	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);

	const [fName, setFName] = useState(user.fName);
	const [lName, setLName] = useState(user.lName);
	const [userName, setUserName] = useState(user.userName);
	const [externalId, setExternalId] = useState(user.externalID ?? "");
	const [dateOfBirth, setDateOfBirth] = useState(
		user.dateOfBirth ? (new Date(user.dateOfBirth).toISOString().slice(0, 10) ?? "") : ""
	);
	const [grade, setGrade] = useState<number | undefined>(user.gradeId);
	const [phone, setPhone] = useState(user.phone ?? "");
	const [email, setEmail] = useState(user.emailAddress ?? "");

	const [showDemographicSection, setShowDemographicSection] = useState(false);
	const [ethnicity, setEthnicity] = useState(user.ethnicity);
	const [gender, setGender] = useState(user.gender);
	const [economicallyDisadvantaged, setEconomicallyDisadvantaged] = useState(
		user.economicallyDisadvantaged
	);
	const [limitedEnglish, setLimitedEnglish] = useState(user.limitedEnglish);
	const [disability, setDisability] = useState(user.disability);

	const showOutsideSite =
		!config?.isUtahComposeSite &&
		(props.userInfo.showCleverID || props.userInfo.showClassLinkID);
	const outsideSiteId = props.userInfo.showCleverID
		? (props.userInfo.cleverID ?? "")
		: (props.userInfo.classLinkID ?? "");
	const [isOutsideSettingsFormOpen, setIsOutsideSettingsFormOpen] = useState(false);

	const [showBadDataWarning, setShowBadDataWarning] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [disableSave, setDisableSave] = useState(false);

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [editUser] = useEditUserMutation();

	useEffect(() => {
		if (
			!fName ||
			!lName ||
			!userName ||
			(dateOfBirth &&
				dateOfBirth > todayDate &&
				props.userInfo.role === UserRole.Writer &&
				!props.userInfo.isTeachersWriterAccount) ||
			(!grade &&
				props.userInfo.role === UserRole.Writer &&
				!props.userInfo.isTeachersWriterAccount) ||
			(!externalId &&
				props.userInfo.role === UserRole.Writer &&
				!props.userInfo.isTeachersWriterAccount &&
				user.externalID) ||
			(!email && props.userInfo.editModel.isEmailAddressRequired)
		) {
			setShowBadDataWarning(true);
			setDisableSave(true);
		} else {
			setShowBadDataWarning(false);
			setDisableSave(false);
		}
	}, [fName, lName, userName, dateOfBirth, grade, externalId, email]);

	useEffect(() => {
		const genderOptions: SimpleOption[] = [{ value: "", label: "--" }];
		Object.entries(Gender).map(([key, value]) => {
			genderOptions.push({ value: key, label: value });
		});
		setGenderOptions(genderOptions);

		const ethnicityOptions: SimpleOption[] = [{ value: "", label: "--" }];
		Object.entries(Ethnicity).map(([key, value]) => {
			ethnicityOptions.push({ value: key, label: value });
		});
		setEthnicityOptions(ethnicityOptions);
	}, []);

	useEffect(() => {
		const gradeOptions: SimpleOption[] =
			grades?.map((grade) => {
				return {
					value: grade.gradeId,
					label: grade.name,
				} as SimpleOption;
			}) ?? [];
		setGradeOptions(gradeOptions);
	}, [grades]);

	function toggleShowDemographicSection() {
		setShowDemographicSection(!showDemographicSection);
	}

	function updateModel(): UserEditModel {
		const model: UserEditModel = {
			...user,
			fName,
			lName,
			externalID: externalId,
			emailAddress: email,
			phone,
			userName,
			dateOfBirth,
			gradeId: grade,
			ethnicity,
			gender,
			economicallyDisadvantaged,
			disability,
			limitedEnglish,
		};
		return model;
	}

	function handleSave() {
		const model = updateModel();
		editUser({ id: props.userInfo.id, editModel: model })
			.unwrap()
			.then(() => {
				props.onClose();
			})
			.catch((error) => {
				setErrorMessage(error.data);
			});
	}

	function handleCancel() {
		props.onClose();
	}

	const handleOutsideSettingsFormClose = () => setIsOutsideSettingsFormOpen(false);

	return (
		<section>
			<div>
				{showBadDataWarning && (
					<AlertMessage
						className="mt-2 text-sm"
						type={AlertLevel.Warning}
						message={`Please ensure all required fields are complete${props.userInfo.role === UserRole.Writer ? " and that the date of birth is today or earlier" : ""}.`}
						showIcon={true}
					/>
				)}
				{errorMessage && (
					<AlertMessage
						className="text-sm mt-4 py-2 !px-4"
						type={AlertLevel.Error}
						message={errorMessage}
						showIcon={true}
					/>
				)}
				<div className="pb-2 pt-3 grid grid-cols-2 gap-3">
					<Field>
						<Label htmlFor="first-name" className="font-medium text-sm">
							First Name
							<RequiredField />
						</Label>
						<Input
							type="text"
							name="first-name"
							id="first-name"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							required
							maxLength={props.userInfo.rules.firstNameMaxLength}
							value={fName}
							onChange={(e) => setFName(e.target.value)}
						/>
					</Field>
					<Field>
						<div className="flex justify-between items-end">
							<Label htmlFor="last-name" className="font-medium text-sm">
								Last Name
								<RequiredField />
							</Label>
							<p className="text-xs leading-6 text-gray-600">
								<RequiredField className="!text-sm" /> - Denotes a required field
							</p>
						</div>
						<Input
							type="text"
							name="last-name"
							id="last-name"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							required
							maxLength={props.userInfo.rules.lastNameMaxLength}
							value={lName}
							onChange={(e) => setLName(e.target.value)}
						/>
					</Field>

					{isHigherThanDistrictAdmin(props.userInfo.editorsRole) && (
						<Field className="col-span-2">
							<Label htmlFor="username" className="font-medium text-sm">
								Username
								<RequiredField />
							</Label>
							<Input
								type="text"
								name="username"
								id="username"
								className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								required
								minLength={props.userInfo.rules.userNameMinLength}
								maxLength={props.userInfo.rules.userNameMaxLength}
								value={userName}
								onChange={(e) => setUserName(e.target.value)}
							/>
						</Field>
					)}

					{!isAdminRole(props.userInfo.role) && (
						<Field className={`${showOutsideSite ? "col-span-1" : "col-span-full"}`}>
							<Label htmlFor="external-id" className="font-medium text-sm">
								{externalIdLabel(props.userInfo.role, config)}
								{props.userInfo.role === UserRole.Writer &&
									!props.userInfo.isTeachersWriterAccount &&
									user.externalID && <RequiredField />}
							</Label>
							<Input
								type="text"
								name="external-id"
								id="external-id"
								required={
									props.userInfo.role === UserRole.Writer &&
									!props.userInfo.isTeachersWriterAccount &&
									!!user.externalID
								}
								className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								maxLength={props.userInfo.rules.externalIdMaxLength}
								value={externalId}
								onChange={(e) => setExternalId(e.target.value)}
							/>
						</Field>
					)}
					{props.userInfo.editorsRole === UserRole.Admin && showOutsideSite && (
						<Field
							className={`${!isAdminRole(props.userInfo.role) ? "col-span-1" : "col-span-full"}`}>
							<Label htmlFor="classlinkClever" className="font-medium text-sm">
								{props.userInfo.showCleverID ? "Clever" : "ClassLink"} ID
							</Label>
							<div className="flex mt-1">
								<Input
									id="classlinkClever"
									name="classlinkClever"
									type="text"
									readOnly
									className="block w-full rounded-none rounded-l-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={outsideSiteId}
								/>
								<Button
									id="outside-site-form-button"
									className="rounded-lg rounded-l-none border-0 ring-1 ring-inset ring-gray-900/20 cursor-pointer flex justify-center overflow-hidden"
									onClick={() => setIsOutsideSettingsFormOpen(true)}>
									<ClassLinkOrCleverIcon
										iconClasses="h-9 w-10 !rounded-lg !rounded-l-none"
										isClassLinkEntity={props.userInfo.showClassLinkID}
										isCleverEntity={props.userInfo.showCleverID}
									/>
								</Button>
							</div>
							{isOutsideSettingsFormOpen && (
								<ModalFrame
									open={isOutsideSettingsFormOpen}
									title={`${props.userInfo.showCleverID ? "Clever Settings" : "ClassLink Settings"}`}
									onClose={handleOutsideSettingsFormClose}>
									<UserOutsideSiteSettingsForm
										userId={props.userInfo.id}
										isCleverForm={props.userInfo.showCleverID}
										onClose={handleOutsideSettingsFormClose}
									/>
								</ModalFrame>
							)}
						</Field>
					)}
					{props.userInfo.role === UserRole.Writer && (
						<>
							<Field>
								<Label htmlFor="dob" className="font-medium text-sm">
									Date of Birth
								</Label>
								<Input
									type="date"
									name="dob"
									id="dob"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={dateOfBirth}
									max={todayDate}
									onChange={(e) => setDateOfBirth(e.target.value)}
								/>
							</Field>
							<Field>
								<Select
									value={grade}
									setValue={(v) => setGrade(v as number)}
									label="Grade"
									requiredField={!props.userInfo.isTeachersWriterAccount}
									prompt="Pick a grade..."
									simpleListBoxId="grade"
									options={gradeOptions}
									fieldClasses="md:pt-0"
									simpleListBoxClasses="w-full"
								/>
							</Field>
						</>
					)}
					{props.userInfo.role !== UserRole.Writer && (
						<div className="grid grid-cols-3 gap-3 col-span-2">
							<Field className="col-span-2">
								<Label htmlFor="email" className="font-medium text-sm">
									Email
									{props.userInfo.editModel.isEmailAddressRequired && (
										<RequiredField />
									)}
								</Label>
								<Input
									id="email"
									name="email"
									type="text"
									required={props.userInfo.editModel.isEmailAddressRequired}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									maxLength={props.userInfo.rules.emailMaxLength}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</Field>
							<Field>
								<Label htmlFor="phone" className="font-medium text-sm">
									Phone
								</Label>
								<Input
									id="phone"
									name="phone"
									type="text"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									maxLength={props.userInfo.rules.phoneMaxLength}
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
								/>
							</Field>
						</div>
					)}
				</div>
			</div>
			{props.userInfo.role === UserRole.Writer &&
				(!config?.isUtahComposeSite ||
					(config?.isUtahComposeSite &&
						isHigherThanDistrictAdmin(props.userInfo.editorsRole))) && (
					<div>
						<div className="flex items-center">
							{showDemographicSection ? (
								<ChevronDownIcon
									tabIndex={0}
									className="size-5 hover:cursor-pointer"
									onClick={toggleShowDemographicSection}
									onKeyDown={(e) =>
										handleEnterSpaceKey(e, toggleShowDemographicSection)
									}
								/>
							) : (
								<ChevronRightIcon
									tabIndex={0}
									className="size-5 hover:cursor-pointer"
									onClick={toggleShowDemographicSection}
									onKeyDown={(e) =>
										handleEnterSpaceKey(e, toggleShowDemographicSection)
									}
								/>
							)}
							<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
								Demographic Information
							</h3>
						</div>
						{showDemographicSection && (
							<div className="pb-2 pt-1 grid grid-cols-2 gap-3">
								<Field>
									<Select
										value={ethnicity}
										setValue={(v) => setEthnicity(v as string)}
										label="Ethnicity"
										simpleListBoxId="ethnicity"
										options={ethnicityOptions}
										fieldClasses="md:pt-0"
										simpleListBoxClasses="w-full"
									/>
								</Field>
								<Field>
									<Select
										value={gender}
										setValue={(v) => setGender(v as string)}
										label="Gender"
										simpleListBoxId="gender"
										options={genderOptions}
										fieldClasses="md:pt-0"
										simpleListBoxClasses="w-full"
									/>
								</Field>
								<Field>
									<Select
										value={economicallyDisadvantaged?.toString() ?? ""}
										setValue={(v) =>
											setEconomicallyDisadvantaged(
												v === "" ? undefined : v === "true"
											)
										}
										label="Economically Disadvantaged"
										simpleListBoxId="economicallyDisadvantaged"
										options={YesNoOptions}
										fieldClasses="md:pt-0"
										simpleListBoxClasses="w-full"
									/>
								</Field>
								<Field>
									<Select
										value={limitedEnglish?.toString() ?? ""}
										setValue={(v) =>
											setLimitedEnglish(v === "" ? undefined : v === "true")
										}
										label="Limited English"
										simpleListBoxId="limited-english"
										options={YesNoOptions}
										fieldClasses="md:pt-0"
										simpleListBoxClasses="w-full"
									/>
								</Field>
								<Field className="col-span-2">
									<Label htmlFor="disability" className="font-medium text-sm">
										Disability
									</Label>
									<Input
										type="number"
										name="disability"
										id="disability"
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										placeholder="--"
										value={disability}
										onChange={(e) => setDisability(Number(e.target.value))}
									/>
								</Field>
							</div>
						)}
					</div>
				)}
			<div className="mt-4 grid grid-cols-2 gap-3">
				<Button
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					id="confirm-button"
					disabled={disableSave}
					onClick={handleSave}
					onKeyDown={(e) => handleEnterKey(e, handleSave)}
					className="btn btn-primary">
					Save
				</Button>
			</div>
		</section>
	);
}
